// src/components/Layout.jsx
import { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Outlet } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import CareerNav from "./CareerNav";
import PolicyNav from "./PolicyNav";

const SUPPORTED_LANGUAGES = ['de', 'en', 'es', 'fr', 'th', 'ko', 'ja', 'zh-cn', 'zh-tw'];

export default function Layout() {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  
  // 경로에서 언어 코드를 직접 파싱 (React Router 파라미터가 하이픈 포함 언어 코드를 제대로 파싱하지 못할 수 있음)
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const actualLang = pathSegments.length > 0 ? pathSegments[0] : null;

  useEffect(() => {
    // 경로에서 직접 파싱한 언어 코드를 우선 사용 (하이픈 포함 언어 코드 지원)
    const langToUse = actualLang && SUPPORTED_LANGUAGES.includes(actualLang) ? actualLang : (lang && SUPPORTED_LANGUAGES.includes(lang) ? lang : 'en');
    
    if (langToUse && SUPPORTED_LANGUAGES.includes(langToUse)) {
      // changeLanguage 호출 (Promise 방식)
      i18n.changeLanguage(langToUse).then(() => {
        // resolvedLanguage가 여전히 올바르게 설정되지 않은 경우 강제로 설정
        if (i18n.resolvedLanguage !== langToUse && i18n.hasResourceBundle(langToUse, 'common')) {
          // i18next의 내부 store를 직접 업데이트
          if (i18n.store) {
            // store의 language와 resolvedLanguage 모두 설정
            i18n.store.language = langToUse;
            i18n.store.resolvedLanguage = langToUse;
            
            // languageChanged 이벤트를 강제로 발생시켜 모든 컴포넌트 재렌더링
            i18n.emit('languageChanged', langToUse);
          }
          
          // i18n 객체의 resolvedLanguage도 직접 설정 시도
          if (i18n.resolvedLanguage !== langToUse) {
            Object.defineProperty(i18n, 'resolvedLanguage', {
              value: langToUse,
              writable: true,
              configurable: true
            });
          }
        }
      }).catch((error) => {
        // 에러 발생 시 기본값으로 설정
        i18n.changeLanguage('en');
      });
    } else {
      i18n.changeLanguage('en');
    }
  }, [lang, actualLang, i18n]);

  useEffect(() => {
    const setHeaderHeight = () => {
      const header = document.querySelector('header');
      if (!header) return;
      const h = Math.ceil(header.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    };

    setHeaderHeight();

    const onResize = () => setHeaderHeight();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    let ro;
    const headerEl = document.querySelector('header');
    if (headerEl && 'ResizeObserver' in window) {
      ro = new ResizeObserver(setHeaderHeight);
      ro.observe(headerEl);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(setHeaderHeight).catch(() => { });
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  return (
    <div className="layout-container">
      <Header />
      <main className="main-content">
        {['careers','join-us','benefits','our-culture','life-at-edge'].includes(pathSegments[1]) && <CareerNav />}
        {['privacy','policies','servicepolicy','youthpolicy','upp','cookies'].includes(pathSegments[1]) && <PolicyNav />}
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
